import React, { useEffect, useRef, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Link } from 'react-router-dom';
import rolodexLogo from '../assets/rolodex.webp';
import cartIcon from '../assets/cart-icon.webp';
import uploadPhotoIcon from '../assets/upload-photo-icon.webp';
import { api } from '../api';
import Avatar from '@mui/material/Avatar';
import { cacheProfile, getCachedProfile } from '../profileCache';

function Header({ onLogout }) {
  const headerRef = useRef(null);
  const [indexingCount, setIndexingCount] = useState(0);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profile, setProfile] = useState(getCachedProfile);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;
    const updateHeaderHeight = () => {
      document.documentElement.style.setProperty('--app-header-height', `${header.getBoundingClientRect().height}px`);
    };
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--app-header-height');
    };
  }, []);

  useEffect(() => {
    const loadInbox = async () => {
      try { const { data } = await api.get('/v1/inbox'); setUnreadCount(data.unreadCount || 0); } catch { setUnreadCount(0); }
    };
    const refreshProfile = () => api.get('/v1/profile/me').then(({ data }) => setProfile(cacheProfile(data))).catch(() => {});
    const cachedProfile = getCachedProfile();
    if (!cachedProfile) refreshProfile();
    const handleProfileChanged = event => setProfile(event.detail);
    window.addEventListener('profile:changed', handleProfileChanged);
    loadInbox();
    window.addEventListener('inbox:changed', loadInbox);
    return () => {
      window.removeEventListener('inbox:changed', loadInbox);
      window.removeEventListener('profile:changed', handleProfileChanged);
    };
  }, []);

  useEffect(() => {
    const loadCount = async () => {
      try {
        const { data } = await api.get('/v1/items/indexing/count');
        setIndexingCount(data);
      } catch (err) {
        setIndexingCount(0);
      }
    };
    loadCount();
    window.addEventListener('indexing:changed', loadCount);
    return () => window.removeEventListener('indexing:changed', loadCount);
  }, []);

  return (
    <header ref={headerRef} className="app-header" style={{
      width: '100%',
      boxSizing: 'border-box',
      padding: '0.5rem',
      background: '#FAF9F6', // this equates to rgba(250, 249, 246, 1)
      borderBottom: '2px solid #222',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      <Link to="/" className="header-brand" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
        <img
          src={rolodexLogo}
          alt="Digidex Logo"
          style={{ height: '3rem', width: '3rem', objectFit: 'contain' }}
        />
        <h1 className="header-title" style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#333',
          margin: 0
        }}>
          Digidex
        </h1>
      </Link>
      <div className="header-right">
        <nav className="header-nav" aria-label="Primary navigation">
          <Link to="/" className="header-nav-home" style={{ color: '#222', fontWeight: 'bold', textDecoration: 'none' }}>My Dex</Link>
          <Link to="/upload-photos" style={{ color: '#222', fontWeight: 'bold', textDecoration: 'none' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', lineHeight: 1 }}>
              <img src={uploadPhotoIcon} alt="" style={{ display: 'block', height: '2rem', width: '2rem', objectFit: 'contain' }} />
              Upload Photos
          </span>
          </Link>
          <Link to="/indexing" style={{ color: '#222', fontWeight: 'bold', textDecoration: 'none' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <img src={cartIcon} alt="" style={{ height: '2rem', width: '2rem', objectFit: 'contain' }} />
              Indexing ({indexingCount})
          </span>
          </Link>
        </nav>
        <button
          className="account-button label-button label-button-dark"
          onClick={event => setAccountAnchor(event.currentTarget)}
          style={{
            padding: '0.5rem .5rem',
            border: 'none',
            borderRadius: '5px',
            background: '#222',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}><Avatar src={profile?.avatarUrl || undefined} sx={{ width: 24, height: 24, fontSize: '0.8rem', bgcolor: '#777' }}>{profile?.username?.[0]?.toUpperCase() || <AccountCircleIcon fontSize="small" />}</Avatar> <span className="account-button-label">My Account</span></span>
        </button>
        <Menu className="account-menu" anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)}>
          <MenuItem className="account-menu-item" component={Link} to="/digipals" onClick={() => setAccountAnchor(null)}>My Digipals</MenuItem>
          <MenuItem className="account-menu-item" component={Link} to="/inbox" onClick={() => setAccountAnchor(null)} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, minWidth: 180 }}><span>Inbox</span>{unreadCount > 0 && <span style={{ color: '#fff', background: '#d32f2f', borderRadius: '999px', minWidth: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold' }}>{unreadCount}</span>}</MenuItem>
          <MenuItem className="account-menu-item" component={Link} to="/settings" onClick={() => setAccountAnchor(null)}>Settings</MenuItem>
          <MenuItem className="account-menu-item" onClick={() => { setAccountAnchor(null); onLogout(); }}>Logout</MenuItem>
        </Menu>
      </div>
    </header>
  );
}

export default Header;
